import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserX, AlertTriangle, ShieldCheck, ArrowLeft, Trash2 } from 'lucide-react';
import api, { extrairMensagemErro } from '../services/api';
import { useDialog } from '../components/common/DialogContext';
import './DeleteAccount.css';

const DeleteAccount: React.FC = () => {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [estaAutenticado, setEstaAutenticado] = useState(false);
  const [solicitando, setSolicitando] = useState(false);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token_akzicao') || sessionStorage.getItem('token_akzicao');
    setEstaAutenticado(!!token);
  }, []);

  const solicitarEliminacao = async () => {
    const confirmou = await dialog.confirm(
      'Tem a certeza que deseja solicitar a eliminação da sua conta? Esta ação iniciará o processo de remoção dos seus dados pessoais e não pode ser revertida facilmente.',
      'Confirmar Solicitação de Exclusão'
    );

    if (!confirmou) return;

    try {
      setSolicitando(true);
      setErro(null);
      setMensagem(null);
      const resposta = await api.post('/account/deletion-request');
      setMensagem(resposta.data.mensagem || 'Solicitação registada com sucesso.');
    } catch (falha: any) {
      setErro(extrairMensagemErro(falha, 'Erro ao solicitar eliminação de conta.'));
    } finally {
      setSolicitando(false);
    }
  };

  return (
    <main className="workspace-page exclusao-public-page">
      <header className="exclusao-hero">
        <span className="exclusao-kicker"><UserX size={16} /> Privacidade & Controlo</span>
        <h1>Exclusão de Conta e Dados</h1>
        <p>Saiba como solicitar a remoção da sua conta Akzição e entenda como tratamos os seus dados durante este processo.</p>
      </header>

      <div className="exclusao-container">
        {!estaAutenticado && (
          <button type="button" className="botao botao-secundario voltar-login-btn" onClick={() => navigate('/login')}>
            <ArrowLeft size={16} /> Voltar para o Login
          </button>
        )}

        <article className="exclusao-content content-area">
          {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
          {erro && <div className="alerta alerta-erro">{erro}</div>}

          <section className="legal-section">
            <h2>1. O seu direito à exclusão</h2>
            <p>Na Akzição, respeitamos a sua privacidade e o controlo sobre os seus dados pessoais. Em conformidade com as políticas do Google Play e regulamentações de proteção de dados, oferecemos um caminho direto para solicitar a eliminação da sua conta.</p>
          </section>

          <section className="legal-section">
            <h2>2. Como funciona o processo</h2>
            <p>A exclusão de conta não é imediata. Ao submeter o pedido, a sua conta entra num estado de "Solicitação Pendente". A nossa equipa processará o pedido para garantir que todos os dados identificáveis sejam removidos, respeitando as obrigações legais de retenção.</p>
          </section>

          <section className="legal-section">
            <h2>3. Dados que serão eliminados</h2>
            <ul className="privacidade-list">
              <li><strong>Perfil Pessoal:</strong> Nome, fotografia de perfil e descrições personalizadas.</li>
              <li><strong>Credenciais:</strong> E-mail de acesso e palavras-passe (hashes).</li>
              <li><strong>Sessões:</strong> Todos os tokens de acesso ativo serão invalidados.</li>
              <li><strong>Contactos:</strong> Números de telefone e e-mails operacionais vinculados ao perfil.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Dados preservados por lei</h2>
            <p>Para manter a integridade operacional e cumprir obrigações fiscais e de auditoria, certos dados não podem ser eliminados:</p>
            <ul className="privacidade-list">
              <li><strong>Histórico Transacional:</strong> Registos de compras e propostas comerciais concluídas.</li>
              <li><strong>Faturação:</strong> Faturas emitidas e registos de pagamentos de subscrição.</li>
              <li><strong>Logs de Auditoria:</strong> Registos de ações administrativas críticas realizadas na plataforma.</li>
            </ul>
            <div className="exclusao-info-box">
              <h3><ShieldCheck size={18} /> Anonimização</h3>
              <p>Sempre que possível, os dados preservados serão anonimizados, removendo a ligação direta à sua identidade pessoal.</p>
            </div>
          </section>

          <section className="exclusao-actions">
            {estaAutenticado ? (
              <>
                <h2>Solicitar agora</h2>
                <p>Como está autenticado, pode iniciar o processo imediatamente.</p>
                <button
                  className="botao botao-perigo"
                  onClick={solicitarEliminacao}
                  disabled={solicitando}
                >
                  <Trash2 size={16} /> {solicitando ? 'A processar...' : 'Solicitar eliminação definitiva'}
                </button>
              </>
            ) : (
              <>
                <h2>Iniciar solicitação</h2>
                <p>Para garantir que ninguém solicita a exclusão da sua conta sem a sua autorização, é necessário validar a sua identidade.</p>
                <div className="alerta alerta-aviso">
                  <AlertTriangle size={18} /> É necessário iniciar sessão para prosseguir com a exclusão.
                </div>
                <button className="botao botao-primario" onClick={() => navigate('/login')}>
                  Entrar para solicitar exclusão
                </button>
                <p style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
                  Não consegue aceder à sua conta? <button className="botao-link" onClick={() => navigate('/reset-password')}>Recuperar palavra-passe</button> ou contacte o suporte.
                </p>
              </>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default DeleteAccount;
